import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const ARTICLES = [
  {
    slug: "family-trees",
    to: "/family-trees",
    chapter: "Wing I",
    title: "Family Trees",
    subtitle: "Lineages traced across generations and continents.",
    read: "18 min · long-form essay",
    year: "Vol. I",
    accent: "12,480 lineages recorded",
  },
  {
    slug: "celebrate-a-life",
    to: "/celebrate-a-life",
    chapter: "Wing II",
    title: "Celebrate a Life — Memorials",
    subtitle: "Living tributes, obituaries, remembrances.",
    read: "12 min · editorial",
    year: "Vol. I",
    accent: "8,214 tributes kept",
  },
  {
    slug: "heritage",
    to: "/category/heritage",
    chapter: "Wing III",
    title: "Heritage",
    subtitle: "Languages, traditions, temples, cuisine and craft.",
    read: "22 min · long-form essay",
    year: "Vol. I",
    accent: "3,097 traditions catalogued",
  },
  {
    slug: "culture",
    to: "/culture",
    chapter: "Wing IV",
    title: "Culture",
    subtitle: "The rituals, textures and tempo of belonging.",
    read: "20 min · long-form essay",
    year: "Vol. I",
    accent: "Global · Ongoing",
  },
  {
    slug: "memorials",
    to: "/category/memorials",
    chapter: "Wing V",
    title: "Memorials",
    subtitle: "Digital shrines. Anniversaries observed. Candles that never burn out.",
    read: "17 min · editorial",
    year: "Vol. I",
    accent: "6,880 memorials",
  },
  {
    slug: "genealogy",
    to: "/category/genealogy",
    chapter: "Wing VI",
    title: "Genealogy",
    subtitle: "Records, DNA, archives — the discipline of finding what was almost lost.",
    read: "24 min · long-form essay",
    year: "Vol. I",
    accent: "97 archives indexed",
  },
  {
    slug: "family-history",
    to: "/category/family-history",
    chapter: "Wing VII",
    title: "Family History",
    subtitle: "Timelines, migrations and the quiet chronicles between two names on a tree.",
    read: "15 min",
    year: "Vol. I",
    accent: "24,110 chronicles",
  },
  {
    slug: "family-stories",
    to: "/category/family-stories",
    chapter: "Wing VIII",
    title: "Family Stories",
    subtitle: "Recipes, letters, songs and oral histories — the folklore of a family.",
    read: "14 min",
    year: "Vol. I",
    accent: "18,760 stories",
  },
  {
    slug: "local-history",
    to: "/category/local-history",
    chapter: "Wing IX",
    title: "Local History",
    subtitle: "Villages, streets and ancestral homes — the geography of memory.",
    read: "16 min",
    year: "Vol. I",
    accent: "5,431 places",
  },
  {
    slug: "obituaries",
    to: "/category/obituaries",
    chapter: "Wing X",
    title: "Obituaries",
    subtitle: "Respectful, researched notices — written to endure the century.",
    read: "10 min",
    year: "Vol. I",
    accent: "11,205 notices",
  },
];

const ease = [0.22, 1, 0.36, 1];

export default function Archive() {
  return (
    <main data-testid="archive-page" className="min-h-screen">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-8 text-center">
        <div className="overline mb-3">§ The Archive</div>
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
          className="heritage-title leading-[1.02] tracking-[-0.01em]"
          style={{ fontSize: "clamp(40px, 6vw, 84px)" }}
        >
          Every article, every wing,
          <br />
          <span className="italic font-italic-serif text-[color:var(--rust)]">
            catalogued for you.
          </span>
        </motion.h1>
        <p className="mt-6 max-w-2xl mx-auto font-italic-serif italic text-lg text-[color:var(--ink-soft)]">
          "A library is not the sum of its shelves — it is a shape of attention.
          Below, the ten wings of the FamilyTree.Team archive, kept in order and
          steadily added to."
        </p>
        <div className="mt-8 flourish w-72 mx-auto" />
      </section>

      {/* Grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24">
        <div className="grid md:grid-cols-2 gap-0 border-t border-l border-[color:var(--sepia)]/30">
          {ARTICLES.map((a, i) => (
            <motion.article
              key={a.slug}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 6) * 0.04, duration: 0.55, ease }}
              className="group border-r border-b border-[color:var(--sepia)]/30 p-8 md:p-10 hover:bg-[color:var(--parchment-2)]/70 transition-colors duration-500"
              data-testid={`archive-card-${a.slug}`}
            >
              <div className="flex items-start justify-between gap-6">
                <div className="min-w-0">
                  <div className="overline text-[color:var(--sepia)] mb-2">
                    {a.chapter} · {a.year}
                  </div>
                  <Link
                    to={a.to}
                    className="heritage-title text-3xl md:text-4xl leading-tight tracking-tight link-underline"
                    data-testid={`archive-link-${a.slug}`}
                  >
                    {a.title}
                  </Link>
                  <p className="mt-3 font-italic-serif italic text-[color:var(--ink-soft)] text-base md:text-lg leading-snug max-w-[46ch]">
                    {a.subtitle}
                  </p>
                </div>
                <ArrowUpRight
                  size={22}
                  className="shrink-0 mt-1 text-[color:var(--sepia)]/50 group-hover:text-[color:var(--rust)] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-500"
                />
              </div>
              <div className="mt-8 flex items-center justify-between numeric-index">
                <span>{a.read}</span>
                <span>{a.accent}</span>
              </div>
            </motion.article>
          ))}
        </div>
      </section>
    </main>
  );
}
